import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Inbox, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { gbp } from "../lib/format";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

export function RejectDialog({ item, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/drawdowns/${item.id}/reject`, { reason }); toast.success(`${item.ref} rejected`); onDone(); onClose(); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Reject {item?.ref}</DialogTitle></DialogHeader>
        <Textarea placeholder="Reason (shown to the partner)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" />
        {err && <div className="text-sm font-medium text-primary" data-testid="reject-error">{err}</div>}
        <DialogFooter><Button variant="destructive" onClick={go} disabled={busy || !reason.trim()} data-testid="reject-confirm-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Reject</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Item({ d, approver, selectable, checked, onCheck, onApprove, onReject }) {
  return (
    <li className="px-4 sm:px-5 py-4" data-testid={`waiting-item-${d.ref}`}>
      <div className="flex items-start gap-3">
        {selectable && <Checkbox checked={checked} onCheckedChange={onCheck} className="mt-1 h-5 w-5" data-testid={`waiting-select-${d.ref}`} />}
        <Link to={`/drawdowns/${d.id}`} className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2"><span className="font-figure text-xs text-muted-foreground">{d.ref}</span><span className="truncate font-semibold">{d.partner_name}</span></div>
          <div className="truncate text-sm text-muted-foreground">{d.project_name}</div>
          <div className="mt-1 font-figure text-sm"><b>{gbp(d.totals.net)}</b> net · {gbp(d.totals.gross)} gross</div>
          {d.flags?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{d.flags.map((f) => <span key={f.key} className="rounded bg-accent px-1.5 py-0.5 text-[11px] font-semibold text-accent-foreground">{f.label}{f.amount ? ` ${gbp(f.amount)}` : ""}</span>)}</div>}
          {d.variance_explanation && <div className="mt-1 text-xs italic text-muted-foreground">“{d.variance_explanation}”</div>}
        </Link>
      </div>
      {approver && (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:justify-end">
          <Button variant="outline" className="h-11 sm:h-9" onClick={() => onReject(d)} data-testid={`waiting-reject-${d.ref}`}>Reject</Button>
          <Button className="h-11 sm:h-9 bg-[hsl(var(--positive))] hover:bg-[hsl(var(--positive)/0.9)]" onClick={() => onApprove(d)} data-testid={`waiting-approve-${d.ref}`}>Approve</Button>
        </div>
      )}
    </li>
  );
}

export function WaitingTray({ items = [], approver = false, onChange = () => {} }) {
  const [sel, setSel] = useState([]);
  const [rej, setRej] = useState(null);
  const [busy, setBusy] = useState(false);
  const clean = items.filter((d) => d.classification === "Clean");
  const look = items.filter((d) => d.classification !== "Clean");
  const approveOne = async (d) => {
    try { await api.post(`/drawdowns/${d.id}/approve`, {}); toast.success(`${d.ref} approved`); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const bulk = async () => {
    setBusy(true);
    try { const { data } = await api.post("/drawdowns/bulk-approve", { ids: sel }); toast.success(`Approved ${data.approved.length} items`); setSel([]); onChange(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const group = (title, list, selectable, tid) => list.length > 0 && (
    <div data-testid={tid}>
      <div className="flex items-center justify-between bg-muted/60 px-4 sm:px-5 py-2">
        <span className="eyebrow flex items-center gap-1.5">{selectable ? <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--positive))]" /> : <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--warning))]" />}{title} · {list.length}</span>
        {selectable && approver && (
          <div className="flex items-center gap-2">
            <button className="text-xs font-semibold text-primary" onClick={() => setSel(sel.length === list.length ? [] : list.map((d) => d.id))} data-testid="waiting-select-all-clean">{sel.length === list.length ? "Clear" : "Select all"}</button>
            <Button size="sm" disabled={!sel.length || busy} onClick={bulk} data-testid="approve-selected-btn">{busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}Approve selected ({sel.length})</Button>
          </div>
        )}
      </div>
      <ul className="divide-y">{list.map((d) => <Item key={d.id} d={d} approver={approver} selectable={selectable && approver} checked={sel.includes(d.id)}
        onCheck={(v) => setSel(v ? [...sel, d.id] : sel.filter((x) => x !== d.id))} onApprove={approveOne} onReject={setRej} />)}</ul>
    </div>
  );
  return (
    <section className="rise rounded-lg border-2 border-primary/30 bg-card overflow-hidden" data-testid="waiting-for-you-tray">
      <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-full bg-primary text-primary-foreground"><Inbox className="h-5 w-5" /></span>
          <div>
            <h2 className="font-heading text-lg font-bold leading-tight">Waiting for you</h2>
            <p className="text-xs text-muted-foreground">{approver ? "Drawdowns checked by GM, ready for your decision" : "Drawdowns awaiting your GM check"}{items.length > 0 && <> · <span data-testid="waiting-clean-count">{clean.length}</span> clean · <span data-testid="waiting-look-count">{look.length}</span> need a look</>}</p>
          </div>
        </div>
        <span className="font-figure text-2xl font-bold" data-testid="waiting-count">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="flex items-center gap-3 px-5 py-6" data-testid="waiting-empty-state">
          <CheckCircle2 className="h-6 w-6 shrink-0 text-[hsl(var(--positive))]" />
          <div><div className="font-semibold">Nothing waiting for your approval</div><div className="text-sm text-muted-foreground">New drawdowns appear here the moment they need you.</div></div>
        </div>
      ) : (<>{group("Clean", clean, true, "waiting-clean-group")}{group("Needs a look", look, false, "waiting-look-group")}</>)}
      <RejectDialog item={rej} onClose={() => setRej(null)} onDone={onChange} />
    </section>
  );
}
